import React, { useState } from "react";
import { Dialog, DialogContent } from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "components/ui/tabs";
import {
  Home,
  Bus,
  Calendar,
  Phone,
  MapPin,
  Layers,
  Copy,
  Check,
} from "lucide-react";
import toast from "react-hot-toast";

export default function ClientTimelineModal({ isOpen, onClose, client, onEdit }) {
  const [copiado, setCopiado] = useState(false);

  if (!client) return null;

  const chales = client.chales || [];
  const excursions = client.excursions || [];

  const copiarTelefone = () => {
    if (!client.phone) return;
    const raw = String(client.phone);
    navigator.clipboard.writeText(raw);
    setCopiado(true);
    toast.success(`Telefone ${raw} copiado para o Bot!`, { icon: "📋", duration: 2000 });
    setTimeout(() => setCopiado(false), 2000);
  };

  // Unifica todas as reservas em ordem cronológica decrescente
  const timelineEvents = [
    ...chales.map((c) => {
      const d = c.checkInDate?.toDate ? c.checkInDate.toDate() : new Date(c.checkInDate || 0);
      return {
        id: c.id,
        type: "chale",
        date: d,
        title: `Estadia no Chalé ${c.chaletNumber || ""}`,
        subtitle: `Check-in: ${d.toLocaleDateString("pt-BR")}${c.checkOutDate ? ` • Check-out: ${new Date(c.checkOutDate?.toDate ? c.checkOutDate.toDate() : c.checkOutDate).toLocaleDateString("pt-BR")}` : ""}`,
        amount: c.totalAmount || c.valorTotal || 0,
        status: c.paymentStatus === "paid" ? "Pago" : "Pendente/Reserva",
        raw: c,
      };
    }),
    ...excursions.map((e) => {
      const d = e.excursionDate?.toDate ? e.excursionDate.toDate() : new Date(e.excursionDate || 0);
      return {
        id: e.id,
        type: "excursao",
        date: d,
        title: `Excursão ${e.city ? `(${e.city})` : ""}`,
        subtitle: `Data: ${d.toLocaleDateString("pt-BR")} • ${e.quantity || 0} passageiros • ${e.vehicle || "Ônibus"}`,
        amount: e.totalAmount || e.valorTotal || 0,
        status: e.status || "Confirmado",
        raw: e,
      };
    }),
  ].sort((a, b) => b.date - a.date);

  const getInitials = (name) => {
    if (!name) return "CL";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[95vw] sm:max-w-2xl max-h-[92vh] overflow-y-auto p-4 sm:p-6 space-y-4">
        {/* Header do Perfil do Cliente */}
        <div className="bg-gradient-to-br from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-4 sm:p-6 shadow-md relative overflow-hidden">
          <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-emerald-500 to-teal-400 text-white font-bold text-xl flex items-center justify-center shadow-md">
                {getInitials(client.name)}
              </div>
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-white">
                    {client.name}
                  </h2>
                  {client.modalidade === "ambos" && (
                    <span className="text-[11px] font-semibold bg-purple-500/20 text-purple-200 border border-purple-400/30 px-2.5 py-0.5 rounded-full flex items-center gap-1">
                      <Layers className="w-3 h-3" /> Chalé + Excursão
                    </span>
                  )}
                  {client.modalidade === "chale" && (
                    <span className="text-[11px] font-semibold bg-emerald-500/20 text-emerald-200 border border-emerald-400/30 px-2.5 py-0.5 rounded-full flex items-center gap-1">
                      <Home className="w-3 h-3" /> Hóspede Chalé
                    </span>
                  )}
                  {client.modalidade === "excursao" && (
                    <span className="text-[11px] font-semibold bg-blue-500/20 text-blue-200 border border-blue-400/30 px-2.5 py-0.5 rounded-full flex items-center gap-1">
                      <Bus className="w-3 h-3" /> Organizador Excursão
                    </span>
                  )}
                  {client.modalidade === "nenhuma" && (
                    <span className="text-[11px] font-semibold bg-slate-500/20 text-slate-300 border border-slate-400/30 px-2.5 py-0.5 rounded-full">
                      Sem Reservas
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-3 text-xs text-slate-300">
                  <span className="flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5 text-emerald-400" />
                    {client.phone}
                  </span>
                  {client.city && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5 text-amber-400" />
                      {client.city}
                    </span>
                  )}
                  {client.cpf && (
                    <span className="text-slate-400">CPF: {client.cpf}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Ações Rápidas no Header */}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              {client.phone && (
                <button
                  type="button"
                  onClick={copiarTelefone}
                  className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 h-10 px-4 rounded-xl text-xs font-semibold shadow-md active:scale-95 transition-all border ${
                    copiado
                      ? "bg-emerald-500 text-white border-emerald-600 ring-2 ring-emerald-400/40"
                      : "bg-emerald-600/30 hover:bg-emerald-600/50 text-emerald-300 border-emerald-500/40"
                  }`}
                  title="Copiar telefone bruto do banco para o Bot WhatsApp"
                >
                  {copiado ? (
                    <>
                      <Check className="w-4 h-4" />
                      <span>Copiado para o Bot!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      <span>Copiar Telefone (Bot)</span>
                    </>
                  )}
                </button>
              )}
              <Button
                variant="outline"
                size="sm"
                className="h-10 px-3 text-xs bg-white/10 hover:bg-white/20 text-white border-white/20"
                onClick={() => {
                  onClose();
                  if (onEdit) onEdit(client);
                }}
              >
                Editar
              </Button>
            </div>
          </div>

          {/* Cards de Métricas do Cliente */}
          <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-white/10 text-center">
            <div className="bg-white/5 rounded-xl p-2.5">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 block font-medium">
                Chalés
              </span>
              <span className="text-lg sm:text-xl font-bold text-emerald-400">
                {client.totalChales || 0}
              </span>
              <span className="text-[10px] text-slate-400 block">estadias</span>
            </div>

            <div className="bg-white/5 rounded-xl p-2.5">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 block font-medium">
                Excursões
              </span>
              <span className="text-lg sm:text-xl font-bold text-blue-400">
                {client.totalExcursoes || 0}
              </span>
              <span className="text-[10px] text-slate-400 block">viagens</span>
            </div>

            <div className="bg-white/5 rounded-xl p-2.5">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 block font-medium">
                Última Visita
              </span>
              <span className="text-xs sm:text-sm font-bold text-amber-300 block truncate mt-1">
                {client.ultimaReservaFormatada || "Nenhuma"}
              </span>
              <span className="text-[10px] text-slate-400 block truncate">
                {client.rotuloRecencia || "Sem registro"}
              </span>
            </div>
          </div>
        </div>

        {/* Abas do Histórico */}
        <Tabs defaultValue="timeline" className="w-full">
          <TabsList className="grid w-full grid-cols-3 h-11 bg-muted/60 p-1 rounded-xl">
            <TabsTrigger value="timeline" className="text-xs font-semibold rounded-lg">
              Linha do Tempo ({timelineEvents.length})
            </TabsTrigger>
            <TabsTrigger value="chales" className="text-xs font-semibold rounded-lg">
              🏡 Chalés ({chales.length})
            </TabsTrigger>
            <TabsTrigger value="excursoes" className="text-xs font-semibold rounded-lg">
              🚌 Excursões ({excursions.length})
            </TabsTrigger>
          </TabsList>

          {/* TAB 1: Linha do Tempo Unificada */}
          <TabsContent value="timeline" className="space-y-3 pt-2">
            {timelineEvents.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground border rounded-xl bg-muted/20">
                <Calendar className="w-10 h-10 mx-auto text-muted-foreground/40 mb-2" />
                <p className="font-semibold text-sm">Nenhuma reserva registrada para este cliente.</p>
                <p className="text-xs text-muted-foreground">O telefone não coincide com registros de chalé ou excursão.</p>
              </div>
            ) : (
              <div className="relative pl-6 space-y-4 before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-border">
                {timelineEvents.map((evt, idx) => (
                  <div key={evt.id || idx} className="relative group">
                    {/* Marcador na linha do tempo */}
                    <div
                      className={`absolute -left-6 top-1 w-5 h-5 rounded-full border-2 border-background flex items-center justify-center text-[10px] shadow-sm ${
                        evt.type === "chale"
                          ? "bg-emerald-500 text-white"
                          : "bg-blue-600 text-white"
                      }`}
                    >
                      {evt.type === "chale" ? "🏡" : "🚌"}
                    </div>

                    <div className="bg-card border rounded-xl p-3.5 shadow-sm hover:shadow-md transition-all space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-bold text-sm text-foreground flex items-center gap-1.5">
                          {evt.title}
                        </span>
                        <span
                          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                            evt.type === "chale"
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300"
                              : "bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300"
                          }`}
                        >
                          {evt.status}
                        </span>
                      </div>

                      <p className="text-xs text-muted-foreground">{evt.subtitle}</p>

                      {evt.amount > 0 && (
                        <div className="pt-1 flex items-center justify-between text-xs text-muted-foreground border-t border-border/50">
                          <span>Valor registrado:</span>
                          <span className="font-bold text-foreground">
                            R$ {Number(evt.amount).toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* TAB 2: Apenas Chalés */}
          <TabsContent value="chales" className="space-y-3 pt-2">
            {chales.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground border rounded-xl">
                <Home className="w-8 h-8 mx-auto text-muted-foreground/40 mb-1" />
                <p className="text-xs">Nenhuma estadia em chalé encontrada.</p>
              </div>
            ) : (
              chales.map((c, idx) => {
                const checkIn = c.checkInDate?.toDate ? c.checkInDate.toDate() : new Date(c.checkInDate || 0);
                const checkOut = c.checkOutDate?.toDate ? c.checkOutDate.toDate() : new Date(c.checkOutDate || 0);
                return (
                  <div key={c.id || idx} className="bg-card border rounded-xl p-3.5 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                        <Home className="w-4 h-4" />
                        Chalé {c.chaletNumber || "N/A"}
                      </span>
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300">
                        {c.paymentStatus === "paid" ? "Pago" : "Pendente"}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                      <div>
                        Check-in: <strong className="text-foreground">{checkIn.toLocaleDateString("pt-BR")}</strong>
                      </div>
                      <div>
                        Check-out: <strong className="text-foreground">{checkOut.toLocaleDateString("pt-BR")}</strong>
                      </div>
                    </div>

                    {c.observations && (
                      <p className="text-xs italic text-muted-foreground bg-muted/40 p-2 rounded-lg">
                        "{c.observations}"
                      </p>
                    )}
                  </div>
                );
              })
            )}
          </TabsContent>

          {/* TAB 3: Apenas Excursões */}
          <TabsContent value="excursoes" className="space-y-3 pt-2">
            {excursions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground border rounded-xl">
                <Bus className="w-8 h-8 mx-auto text-muted-foreground/40 mb-1" />
                <p className="text-xs">Nenhuma excursão encontrada.</p>
              </div>
            ) : (
              excursions.map((e, idx) => {
                const date = e.excursionDate?.toDate ? e.excursionDate.toDate() : new Date(e.excursionDate || 0);
                return (
                  <div key={e.id || idx} className="bg-card border rounded-xl p-3.5 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-sm text-blue-600 dark:text-blue-400 flex items-center gap-1.5">
                        <Bus className="w-4 h-4" />
                        Excursão - {e.city || "Cidade não informada"}
                      </span>
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-950/60 text-blue-800 dark:text-blue-300">
                        {e.status || "Confirmado"}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                      <div>
                        Data: <strong className="text-foreground">{date.toLocaleDateString("pt-BR")}</strong>
                      </div>
                      <div>
                        Passageiros: <strong className="text-foreground">{e.quantity || 0} pax</strong>
                      </div>
                      <div>
                        Veículo: <strong className="text-foreground">{e.vehicle || "Ônibus"}</strong>
                      </div>
                      {e.camarote?.requested && (
                        <div>
                          Camarote: <strong className="text-purple-600 font-semibold">Sim</strong>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
